import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, Lock, ArrowLeft, CheckCircle, Warning } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (password !== confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setDone(true);
            toast.success("Password reset successfully");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8FAFC]">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-2.5 mb-10">
                    <div className="h-10 w-10 flex items-center justify-center">
                        <Logo size={40} />
                    </div>
                    <div className="leading-tight">
                        <div className="font-bold text-base text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            ADVANCE NETWORKS
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Technician Portal
                        </div>
                    </div>
                </div>

                <div className="mb-8">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">
                        Set a new password
                    </div>
                    <h2
                        className="text-3xl sm:text-4xl font-bold text-[#003366]"
                        style={{ fontFamily: "Outfit" }}
                        data-testid="reset-password-heading"
                    >
                        Reset your password
                    </h2>
                    <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                        Choose a strong password — at least 8 characters. The link is valid for 30 minutes.
                    </p>
                </div>

                {!token ? (
                    <div
                        className="rounded-md border border-red-200 bg-red-50 p-4 flex gap-3"
                        data-testid="reset-password-missing-token"
                    >
                        <Warning size={22} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-red-900 leading-relaxed">
                            This link is missing a reset token. Please request a new link from the
                            {" "}<Link to="/forgot-password" className="font-semibold underline">forgot password</Link> page.
                        </div>
                    </div>
                ) : done ? (
                    <div className="space-y-5" data-testid="reset-password-success">
                        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
                            <CheckCircle size={22} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-emerald-900 leading-relaxed">
                                Your password has been reset. You can now sign in with your new password.
                            </div>
                        </div>
                        <Button
                            type="button"
                            onClick={() => navigate("/login")}
                            className="w-full h-11 bg-[#0066D9] hover:bg-[#0052ad] text-white font-medium"
                            data-testid="reset-password-go-to-login"
                        >
                            Go to sign in
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                New password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    placeholder="••••••••"
                                    className="h-11 pr-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                    data-testid="reset-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    data-testid="reset-password-toggle-visibility"
                                >
                                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirm" className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Confirm new password
                            </Label>
                            <Input
                                id="confirm"
                                type={showPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                minLength={8}
                                placeholder="••••••••"
                                className="h-11 border-[#E2E8F0] focus-visible:ring-[#0066D9]"
                                data-testid="reset-password-confirm-input"
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-11 bg-[#0066D9] hover:bg-[#0052ad] text-white font-medium"
                            data-testid="reset-password-submit-button"
                        >
                            {submitting ? "Updating..." : (<><Lock size={18} className="mr-2" /> Update password</>)}
                        </Button>
                    </form>
                )}

                <div className="mt-8 pt-6 border-t border-[#E2E8F0] text-sm text-slate-600">
                    <Link
                        to="/login"
                        className="inline-flex items-center text-[#0066D9] font-semibold hover:underline"
                        data-testid="link-back-to-login"
                    >
                        <ArrowLeft size={16} className="mr-1.5" /> Back to sign in
                    </Link>
                </div>
            </div>
        </div>
    );
}
